import { i18n } from "@lingui/core";
import { type I18nContext, LinguiContext } from "@lingui/react";
import { type ReactNode, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import { isRtlLocale, isSupportedLocale, SOURCE_LOCALE, type SupportedLocale } from "~/config/i18n";
import { useUserPreferences } from "~/features/userPreferences";
import { messages as sourceMessages } from "~/locales/en/messages";

import { loadLocale } from "./loadLocale";

// Activate the source locale eagerly at a module load, so the LinguiProvider has
// a non-null locale on its first render. Without this Lingui logs an
// informational warning ("I18nProvider rendered null …") and renders nothing
// until the async `loadLocale` resolves. The source-locale catalog is tiny
// and ships in the main chunk anyway, so the import is free.
if (!i18n.locale) {
  i18n.loadAndActivate({ locale: SOURCE_LOCALE, messages: sourceMessages });
}

interface Props {
  children: ReactNode;
}

const subscribeToLocale = (onChange: () => void) => i18n.on("change", onChange);
const readLocale = () => i18n.locale;

/**
 * One `i18n` identity per locale. Lingui's own provider keeps the singleton in context, so a
 * value memoised on it — the Zod schemas and labels the app builds in `useMemo(…, [i18n])` —
 * never re-keyed after a locale switch and kept the first language. The proxy forwards every
 * read and call to the singleton; only its identity is new per locale.
 */
const contextForLocale = (): I18nContext => ({
  i18n: new Proxy(i18n, {}),
  _: i18n.t,
  defaultComponent: undefined,
});

export const I18nProvider = ({ children }: Props) => {
  const { preferences } = useUserPreferences();
  const locale = useSyncExternalStore(subscribeToLocale, readLocale);
  const [bound, setBound] = useState(() => ({ locale, context: contextForLocale() }));

  if (bound.locale !== locale) {
    setBound({ locale, context: contextForLocale() });
  }

  // `active` mirrors `i18n.locale` for the `<html lang>` / `dir` effect. We
  // seed it from persisted preferences, so the first render is already in the
  // user's language — no SOURCE_LOCALE → user-locale flicker.
  const [active, setActive] = useState<SupportedLocale>(() =>
    isSupportedLocale(preferences.locale) ? preferences.locale : SOURCE_LOCALE,
  );

  // Single source of truth for "what locale do we want active right now?"
  // — derived from the user preference. Recomputing it as a memo (not state)
  // means changes to `preferences.locale` flow through deterministically
  // without an intermediate React state update.
  const desiredLocale = useMemo<SupportedLocale>(
    () => (isSupportedLocale(preferences.locale) ? preferences.locale : SOURCE_LOCALE),
    [preferences.locale],
  );

  // Load + activate the catalog whenever the desired locale changes. The
  // source-locale catalog is already activated at a module load (see top of
  //  the file), so the first run for users staying on `en` is a no-op fast path
  // that resolves synchronously in `loadLocale`. For other locales we await
  // the dynamic import, then `i18n.loadAndActivate` swaps the message table,
  // and Lingui re-renders consumers via its own subscription.
  useEffect(() => {
    let cancelled = false;

    void loadLocale(desiredLocale).then((resolved) => {
      if (cancelled) {
        return;
      }

      // `loadLocale` calls `i18n.loadAndActivate` internally — guard against
      // a stale `resolved` here in case future refactors change that. Also,
      // unconditionally mirror to the local state so the `<html lang>` effect
      // runs even when the active locale is already correct (StrictMode
      // double-mount in dev would otherwise leave `<html lang>` stale).
      if (i18n.locale !== resolved) {
        i18n.activate(resolved);
      }

      setActive(resolved);
    });

    return () => {
      cancelled = true;
    };
  }, [desiredLocale]);

  // Sync `<html lang>` / `dir` to the active locale. Bound to `active` (not
  // `desiredLocale`) so the DOM only reflects locales whose catalogs have
  // actually loaded — avoids a brief inconsistency where the lang attribute
  // is "fr-FR" but the visible text is still the source-locale fallback.
  useEffect(() => {
    document.documentElement.lang = active;
    document.documentElement.dir = isRtlLocale(active) ? "rtl" : "ltr";
  }, [active]);

  return <LinguiContext value={bound.context}>{children}</LinguiContext>;
};
